import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { StarPackContent } from './StarPackContent';
import { StarPackTitle } from './StarPackTitle';
import type { StarPack_cardPack } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  padding: var(--double-unit) var(--double-unit) var(--triple-unit)
    var(--double-unit);
  width: 100%;
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  overflow: hidden;

  &.claimable {
    --border-gradient: linear-gradient(
      to top,
      var(--league-color) 0%,
      transparent 20%
    );
    ${borderGradientStyle};
  }
  @media ${laptopAndAbove} {
    justify-self: flex-end;
  }
`;

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  bottom: 0;
  height: var(--double-unit);
  width: 150%;
`;

type Props = {
  cardPack: StarPack_cardPack;
  children?: ReactNode;
};
export const StarPack = ({ cardPack, children }: PropsWithChildren<Props>) => {
  const { rewardedByTask, primaryColor } = cardPack;

  if (!rewardedByTask || !isType(rewardedByTask, 'StarPackTask')) {
    return null;
  }

  const claimable =
    rewardedByTask.aasmState === ManagerProgressionTaskState.COMPLETED;

  return (
    <Root
      className={classNames({ claimable })}
      style={
        {
          '--league-color': primaryColor,
        } as CSSProperties
      }
    >
      {!!claimable && (
        <StyledNeonEdge
          color={primaryColor}
          showHalo="always"
          showNeon="always"
        />
      )}
      <StarPackTitle cardPack={cardPack} />
      <StarPackContent cardPack={cardPack}>{children}</StarPackContent>
    </Root>
  );
};

StarPack.fragments = {
  cardPack: gql`
    fragment StarPack_cardPack on CardPack {
      slug
      primaryColor: color(variant: PRIMARY)
      rewardedByTask {
        id
        aasmState
      }
      ...StarPackTitle_cardPack
      ...StarPackContent_cardPack
    }
    ${StarPackTitle.fragments.cardPack}
    ${StarPackContent.fragments.cardPack}
  ` as TypedDocumentNode<StarPack_cardPack>,
};
